/* import __COLOCATED_TEMPLATE__ from './company-profile-cost-codes-list-item.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { enqueueTaskGroup, task } from 'ember-concurrency';
import { action } from '@ember/object';
import clsx from 'clsx';
import { makeMoney } from 'sh-www/helpers/make-money';
import { showModal } from 'sh-www/utils/modal-controls';

const CLASS_NAME = 'SH-c-company-profile-cost-codes-list-item';

class CompanyProfileCostCodesListItem extends Component {
  @service('commands.cost-code-commands') costCodeCommandService;
  @service('commands.story-code-commands') storyCodeCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-team-commands') companyTeamCommandService;
  @service ('error-tracker') errorTracker;
  @service session;

  @enqueueTaskGroup updateCostCode;

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get userIsAdmin() {
    const { currentUser } = this.session;
    return currentUser.isCompanyAdmin || currentUser.isAdmin;
  }

  get userCanEditCode() {
    const { adminOnly, type } = this.args.code;

    if (this.isCostCode || adminOnly) {
      return this.userIsAdmin;
    }

    return true;
  }

  /**
   * Is the managed item a CostCode (true) or a StoryCode (false)
   * @return {boolean}
   */
  get isCostCode() {
    return this.args.code.constructor.modelName === 'cost-code';
  }

  get balanceString() {
    const { code } = this.args;

    const balances = [];
    if (code.availableCreditInCents) {
      balances.push(`<span>${makeMoney([code.availableCreditInCents], { inCents: true })} available</span>`);
    }
    if (code.pendingCreditInCents) {
      balances.push(`<span><i>${makeMoney([code.pendingCreditInCents], { inCents: true })} pending</i></span>`);
    }

    return balances.join(' | ');
  }

  #userOnCodeTeam = createCache(() => {
    const { code, userCompanyTeams } = this.args;

    const codeTeamIds = code.companyTeams.reduce((accum, { id }) => {
      accum[id] = true;
      return accum;
    }, {});

    if (Object.keys(codeTeamIds).length === 0) {
      return true;
    }

    const userTeamIds = userCompanyTeams.reduce((accum, { id }) => {
      accum[id] = true;
      return accum;
    }, {});

    if (Object.keys(userTeamIds).filter(id => codeTeamIds.hasOwnProperty(id)).length === 0) {
      return false;
    };

    return true;
  });

  get userOnCodeTeam() {
    return getValue(this.#userOnCodeTeam);
  }

  get canModifyTeamsForCode() {
    const { adminOnly, deactivated } = this.args.code;
    return !deactivated && !adminOnly && this.userCanEditCode;
  }

  get menuTeamOptions() {
    const { code, userCompanyTeams, usingCompanyTeams } = this.args;
    const menuOptions = [];

    if (!usingCompanyTeams || !this.canModifyTeamsForCode) {
      return menuOptions;
    }

    const availableTeams = userCompanyTeams.filter(userTeam => !code.companyTeams
      .find(codeTeam => codeTeam.id === userTeam.id)
    );

    if (this.userIsAdmin && availableTeams.length > 1) {
      menuOptions.push({
        title: 'All Teams',
        onClick: () => this.addCodeToAllTeams.perform(code),
      });
    }

    availableTeams.forEach((team) => {
      menuOptions.push({
        title: team.name,
        onClick: () => this.addCompanyTeamCode.perform(team, code),
      });
    });

    return menuOptions;
  }

  get menuAdditionalOptions() {
    const { adminOnly, deactivated } = this.args.code;
    const menuOptions = [];

    if (this.userCanEditCode && this.userOnCodeTeam) {
      menuOptions.push({
        title: deactivated ? 'Reactivate code' : 'Deactivate code',
        onClick: () => this.toggleCodeDeactivated.perform(),
      });
    }

    if (this.userIsAdmin && !deactivated) {
      menuOptions.push({
        title: adminOnly ? 'Remove admin-only' : 'Make admin-only',
        icon: adminOnly ? 'lock_open' : 'lock',
        onClick: adminOnly ? () => this.toggleCodeAdmin.perform() : this.onMakeAdminOnlyClicked,
      });
    }

    return menuOptions;
  }

  @task({ group: 'updateCostCode' })
  *toggleCodeDeactivated() {
    const { code } = this.args;

    try {
      if (this.isCostCode) {
        yield this.costCodeCommandService.updateCostCode(code, !code.deactivated);
      } else {
        yield this.storyCodeCommandService.updateStoryCode(code, !code.deactivated);
      }

      yield this.args.loadCode.perform(code.id);
    } catch (error) {
      this.applicationStateManager.addToast("Failed to change code's deactivated status");
      this.errorTracker.error('toggleCodeDeactivated - failed to toggle cost deactivated status', this, error);
    }
  }

  @task({ group: 'updateCostCode' })
  *toggleCodeAdmin() {
    const { code } = this.args;

    try {
      if (this.isCostCode) {
        yield this.costCodeCommandService.updateCostCode(code, null, !code.adminOnly);
      } else {
        yield this.storyCodeCommandService.updateStoryCode(code, null, !code.adminOnly);
      }

      yield this.args.loadCode.perform(code.id);
    } catch (error) {
      this.applicationStateManager.addToast("Failed to change code's admin status");
      this.errorTracker.error('toggleCodeAdmin - failed to toggle code admin-only status', this, error);
    }
  }

  @task({ group: 'updateCostCode' })
  *addCompanyTeamCode(team, code) {
    try {
      if (this.isCostCode) {
        yield this.companyTeamCommandService.addCostCode(team.id, code.id);
      } else {
        yield this.companyTeamCommandService.addStoryCode(team.id, code.id);
      }

      yield this.args.loadCode.perform(code.id);
    } catch (error) {
      this.applicationStateManager.addToast('Failed to add team to code');
      this.errorTracker.error(`addCompanyTeamCode - failed to add team id ${team.id} to code id ${code.id}`, this, error);
    }
  }

  @task({ group: 'updateCostCode' })
  *addCodeToAllTeams(code) {
    try {
      if (this.isCostCode) {
        yield this.companyTeamCommandService.addCostCodeToAllTeams(code.company.id, code.id);
      } else {
        yield this.companyTeamCommandService.addStoryCodeToAllTeams(code.company.id, code.id);
      }

      yield this.args.loadCode.perform(code.id);
    } catch (error) {
      this.applicationStateManager.addToast('Failed to add teams to code');
      this.errorTracker.error(`addCodeToAllTeams - failed to add all teams to code id ${code.id}`, this, error);
    }
  }

  @task({ group: 'updateCostCode' })
  *removeCompanyTeamCode(team, code) {
    try {
      if (this.isCostCode) {
        yield this.companyTeamCommandService.removeCostCode(team.id, code.id);
      } else {
        yield this.companyTeamCommandService.removeStoryCode(team.id, code.id);
      }

      yield this.args.loadCode.perform(code.id);
      this.applicationStateManager.addToast('Team removed from code');
    } catch (error) {
      this.applicationStateManager.addToast('Failed to remove team from code');
      this.errorTracker.error(`removeCompanyTeamCode - failed to remove team id ${team.id} from code id ${code.id}`, this, error);
    }
  }

  @action
  onMakeAdminOnlyClicked() {
    const modalModel = {
      titleText: 'Make this code admin-only?',
      bodyText: 'Any non-admin will no longer be able to apply this code. However, all instances of this code on projects will remain.',
      actionButtonText: 'Confirm',
      isShowCancelButton: true,
      confirmAction: () => {
        this.toggleCodeAdmin.perform();
      }
    };

    showModal(this, 'confirmation', modalModel);
  }

  @action
  canRemoveCodeFromTeam(team) {
    return this.canModifyTeamsForCode && this.args.userCompanyTeams.find(userTeam => team.id === userTeam.id);
  }
}

export default CompanyProfileCostCodesListItem;
